import React, { useMemo } from "react";
import { ShoppingCartIcon, Eye } from "lucide-react";
import BuyNowButton from "../../posCommon/BuyNowButton";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addToCart,
  selectCartData,
} from "../../../ReduxSlice/BookSlice";
import CardLoaderSkelton from "./CardLoaderSkelton";

function Card({ searchQuery, isLoading, allBooks }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Get cart data
  const cartItems = useSelector(selectCartData);

  // ✅ Filter books
  const filteredBookData = useMemo(() => {
    if (!searchQuery) return allBooks;
    return allBooks.filter((item) =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, allBooks]);

  // ✅ Add to cart with duplicate check
  const handleAddToCart = (book) => {
    if (book.price === "" || book.price === "-") {
      window.open(book.pdflink, "_blank");
      return;
    }

    const alreadyInCart = cartItems.some(
      (item) => item.id === book.id
    );

    if (alreadyInCart) {
      alert("This book is already added in cart");
      return;
    }

    dispatch(addToCart(book));
  };

  if (isLoading) return <CardLoaderSkelton />;

  return (
    <div className="w-full grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {filteredBookData.map((book) => {
        const isAdded = cartItems.some(
          (item) => item.id === book.id
        );

        return (
          <motion.div
            key={book.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col"
          >
            {/* Image */}
            <div className="h-44 bg-gray-100 flex items-center justify-center">
              <img
                src={book.image}
                alt={book.booknameinenglish}
                className="h-full object-contain"
              />
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-4">
              <h3 className="text-lg font-semibold line-clamp-2 text-gray-800">
                {book.booknameinenglish}
              </h3>

              <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                {book.description}
              </p>

              <div className="mt-3 text-xl font-bold text-indigo-600">
                ₹ {book.price || 0}
              </div>

              {/* Buttons */}
              <div className="mt-auto flex gap-2 pt-3">
                {/* Add Button */}
                <button
                  onClick={() => handleAddToCart(book)}
                  disabled={isAdded}
                  className={`flex-1 flex items-center justify-center gap-1 rounded-lg py-2 text-sm font-semibold transition
                    ${
                      isAdded
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-indigo-600 text-white hover:bg-indigo-700"
                    }`}
                >
                  <ShoppingCartIcon size={16} />
                  {isAdded ? "Added" : "Add"}
                </button>

                {/* Buy Now */}
                <BuyNowButton book={book} />

                {/* View */}
                <button
                  onClick={() => navigate(`/pos/book/${book.id}`)}
                  className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-yellow-500 text-black py-2 text-sm font-semibold hover:bg-yellow-600 transition"
                >
                  <Eye size={16} />
                  View
                </button>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export default Card;
